// Tiny path-based router built on the History API. No external dependency
// (react-router is not installed). Clean paths (no '#') keep the booking links
// shareable and human-readable; the server's SPA fallback serves index.html for
// any non-API, non-asset path so a deep link or a refresh resolves on a fresh
// request. Implements: ADR-0002 section 5 (multi-host: a landing at /, a public
// booking page at /{slug}, admin at /admin, and the token-addressed manage page
// scoped to the booking's host).
//
// Route grammar (path form, e.g. '/<segments>'):
//   /                       -> landing
//   /admin                  -> host admin
//   /{slug}                 -> public booking page for that host
//   /{slug}/manage/{token}  -> manage a booking for that host (cancel/reschedule)
// Reserved first segments (admin, api, assets, manage) can never be a host slug;
// a first segment containing a '.' is a static asset (favicon.ico, *.svg, ...),
// never an app route.

import { useEffect, useState } from 'react';

/**
 * First segments that are app routes or platform paths, never host slugs:
 *   admin   -> the host admin route
 *   api     -> the JSON API surface
 *   assets  -> the built static assets
 *   manage  -> the manage path segment (only valid as the middle of a 3-part
 *              manage route, never a lone first segment)
 */
export const RESERVED_SLUGS = new Set(['admin', 'api', 'assets', 'manage']);

/**
 * True when a first path segment can never be a host slug: it is a reserved word,
 * or it contains a '.' (a static asset like favicon.ico, robots.txt, *.svg, *.png
 * that lives at the bundle root, never an app route).
 */
export function isReservedSlug(first: string): boolean {
  return RESERVED_SLUGS.has(first) || first.includes('.');
}

export type Route =
  | { name: 'landing' }
  | { name: 'admin' }
  // A booking route. With an `eventSlug` it is a private-event page
  // (`/{slug}/{eventSlug}`, ADR-0003 section 3): the BookingPage renders that one
  // event directly, skipping the landing type list. Without it, the normal
  // landing booking page (`/{slug}`).
  | { name: 'booking'; slug: string; eventSlug?: string }
  | { name: 'manage'; slug: string; token: string }
  | { name: 'notfound' };

/** Parse a location.pathname into a typed Route. */
export function parsePath(pathname: string): Route {
  const clean = pathname.replace(/^\//, '');
  const segments = clean.split('/').filter(Boolean).map((s) => decodeURIComponent(s));

  if (segments.length === 0) return { name: 'landing' };

  const first = segments[0] as string;
  if (first === 'admin' && segments.length === 1) return { name: 'admin' };

  // A reserved or dotted first segment can never be a host slug, so it can never
  // be the {slug} of a manage route either (a valid 3-segment manage route
  // requires a non-reserved, non-dotted slug). Everything under such a first
  // segment is a not-found.
  if (isReservedSlug(first)) return { name: 'notfound' };

  // /{slug}
  if (segments.length === 1) return { name: 'booking', slug: first };

  // /{slug}/{eventSlug}: a private-event booking page (ADR-0003 section 3). The
  // second segment must NOT be 'manage' (that is the middle of the 3-segment
  // manage route). `first` is already known non-reserved and non-dotted.
  const second = segments[1] as string;
  if (segments.length === 2 && second !== 'manage') {
    return { name: 'booking', slug: first, eventSlug: second };
  }

  // /{slug}/manage/{token}: slug already known non-reserved and non-dotted.
  if (segments.length === 3 && segments[1] === 'manage' && segments[2]) {
    return {
      name: 'manage',
      slug: first,
      token: segments[2],
    };
  }

  return { name: 'notfound' };
}

/** Build the in-app (path) booking link for a host slug. */
export function bookingPathLink(slug: string): string {
  return `/${encodeURIComponent(slug)}`;
}

/** Absolute in-app booking URL (origin + path) for sharing/copying. */
export function bookingAbsoluteLink(slug: string): string {
  const origin =
    typeof window !== 'undefined' && window.location ? window.location.origin : '';
  return `${origin}${bookingPathLink(slug)}`;
}

/** Build the in-app (path) manage link for a signed booking token under a host. */
export function managePathLink(slug: string, signedToken: string): string {
  return `/${encodeURIComponent(slug)}/manage/${encodeURIComponent(signedToken)}`;
}

/** Absolute in-app manage URL (origin + path) for sharing/copying. */
export function manageAbsoluteLink(slug: string, signedToken: string): string {
  const origin =
    typeof window !== 'undefined' && window.location ? window.location.origin : '';
  return `${origin}${managePathLink(slug, signedToken)}`;
}

/**
 * Navigate to an absolute app path (beginning with '/') via the History API.
 * pushState updates the URL without a reload, then a synthetic popstate event
 * tells useRoute to re-read the new location.
 */
export function navigate(path: string): void {
  if (typeof window === 'undefined') return;
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** React hook: the current parsed route, kept in sync with popstate. */
export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() =>
    parsePath(typeof window !== 'undefined' ? window.location.pathname : '/'),
  );
  useEffect(() => {
    const onChange = (): void => setRoute(parsePath(window.location.pathname));
    window.addEventListener('popstate', onChange);
    // Re-sync once on mount in case the path changed before listeners attached.
    onChange();
    return () => window.removeEventListener('popstate', onChange);
  }, []);
  return route;
}

/**
 * Install a single global click handler that keeps internal navigation client
 * side. For a plain left-click (button 0, no modifier keys) on an <a> whose href
 * is a same-origin internal app path, we preventDefault and navigate() instead of
 * letting the browser do a full page load. External links, new-tab links, API and
 * asset paths, and downloads all fall through to the browser. Defensive: guards
 * typeof window and bails on anything it does not recognise as an internal app
 * link, so it can never swallow a navigation it should not own.
 */
export function useLinkInterception(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onClick = (e: MouseEvent): void => {
      // Only plain left-clicks; let the browser handle modified clicks (open in
      // new tab, download, etc.) and non-primary buttons.
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const target = e.target as Element | null;
      const anchor = target?.closest?.('a');
      if (!anchor) return;

      // A target other than the current frame, a download, or an explicit
      // rel=external opt-out: leave to the browser.
      const targetAttr = anchor.getAttribute('target');
      if (targetAttr && targetAttr !== '_self') return;
      if (anchor.hasAttribute('download')) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      // Internal app path only: starts with a single '/', not a protocol-relative
      // '//host' URL, and not an API or asset path the server owns.
      if (!href.startsWith('/')) return;
      if (href.startsWith('//')) return;
      if (href.startsWith('/api/') || href.startsWith('/assets/')) return;

      e.preventDefault();
      navigate(href);
    };

    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);
}
